import type { UiMessages } from './UiMessagesProvider';

export type UiMessagesInterpolation = Record<string, number | string>;
/** Compatible with both plain translators and react-i18next's typed `TFunction`. */
export type UiMessagesTranslator = {
  bivarianceHack(key: string, interpolation?: UiMessagesInterpolation): string;
}['bivarianceHack'];

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

const CURRENCIES = [
  'usd',
  'eur',
  'gbp',
  'chf',
  'cad',
  'aud',
  'jpy',
  'cny',
  'pln',
  'uah',
  'inr',
  'brl',
] as const;

/**
 * Adapt any translation function to the dependency-free UI messages contract. Keys are relative to
 * the shared `common` namespace (`useTranslation('common')` in react-i18next applications).
 */
export function createUiMessages(translate: UiMessagesTranslator): UiMessages {
  const t = (key: string, interpolation?: UiMessagesInterpolation) =>
    translate(`ui.${key}`, interpolation);

  return {
    badge: { remove: t('badge.remove') },
    breadcrumbs: { root: t('breadcrumbs.root') },
    calendar: {
      previousMonth: t('calendar.previousMonth'),
      nextMonth: t('calendar.nextMonth'),
      months: MONTHS.map((month) => t(`calendar.months.${month}`)),
      weekdaysShort: WEEKDAYS.map((day) => t(`calendar.weekdaysShort.${day}`)),
      weekdaysFull: WEEKDAYS.map((day) => t(`calendar.weekdaysFull.${day}`)),
    },
    carousel: {
      root: t('carousel.root'),
      previous: t('carousel.previous'),
      next: t('carousel.next'),
      slide: (index, total) => t('carousel.slide', { current: index + 1, total }),
      goToSlide: (index) => t('carousel.goToSlide', { index: index + 1 }),
    },
    closeButton: { close: t('closeButton.close') },
    currencyInput: {
      currency: t('currencyInput.currency'),
      names: Object.fromEntries(
        CURRENCIES.map((currency) => [
          currency.toUpperCase(),
          t(`currencyInput.names.${currency}`),
        ]),
      ),
    },
    datePicker: {
      today: t('datePicker.today'),
      cancel: t('datePicker.cancel'),
      apply: t('datePicker.apply'),
      startPlaceholder: t('datePicker.startPlaceholder'),
      endPlaceholder: t('datePicker.endPlaceholder'),
    },
    filter: {
      open: t('filter.open'),
      sheetTitle: t('filter.sheetTitle'),
      close: t('filter.close'),
      add: t('filter.add'),
      clear: t('filter.clear'),
      apply: t('filter.apply'),
      remove: t('filter.remove'),
      emptyTitle: t('filter.emptyTitle'),
      emptyText: t('filter.emptyText'),
      field: t('filter.field'),
      selectField: t('filter.selectField'),
      operator: t('filter.operator'),
      value: t('filter.value'),
      selectValue: t('filter.selectValue'),
      enterValue: t('filter.enterValue'),
      valueFrom: t('filter.valueFrom'),
      valueTo: t('filter.valueTo'),
      rangeFrom: t('filter.rangeFrom'),
      rangeTo: t('filter.rangeTo'),
      clearDate: t('filter.clearDate'),
      selected: (count) => t('filter.selected', { count }),
      operatorEquals: t('filter.operatorEquals'),
      operatorContains: t('filter.operatorContains'),
      operatorBetween: t('filter.operatorBetween'),
      operatorOnOrBefore: t('filter.operatorOnOrBefore'),
      operatorGreaterThan: t('filter.operatorGreaterThan'),
      operatorLessThan: t('filter.operatorLessThan'),
    },
    mobileNavDrawer: {
      open: t('mobileNavDrawer.open'),
      close: t('mobileNavDrawer.close'),
    },
    modal: {
      close: t('modal.close'),
      dontShowAgain: t('modal.dontShowAgain'),
      settings: t('modal.settings'),
    },
    multiSelect: {
      placeholder: t('multiSelect.placeholder'),
      noResults: t('multiSelect.noResults'),
      reset: t('multiSelect.reset'),
      selectAll: t('multiSelect.selectAll'),
      remove: (label) => t('multiSelect.remove', { label }),
    },
    notification: { close: t('notification.close') },
    notificationProvider: { region: t('notificationProvider.region') },
    otpInput: { digit: t('otpInput.digit') },
    pagination: {
      root: t('pagination.root'),
      previous: t('pagination.previous'),
      next: t('pagination.next'),
      pageInfo: (page, totalPages) => t('pagination.pageInfo', { page, total: totalPages }),
      goToPage: (page) => t('pagination.goToPage', { page }),
    },
    paginationDots: {
      slides: t('paginationDots.slides'),
      goToSlide: (index) => t('paginationDots.goToSlide', { index: index + 1 }),
    },
    phoneInput: { country: t('phoneInput.country') },
    progress: { root: t('progress.root') },
    select: {
      placeholder: t('select.placeholder'),
      noResults: t('select.noResults'),
      create: (value) => t('select.create', { value }),
    },
    slider: {
      minimum: t('slider.minimum'),
      maximum: t('slider.maximum'),
      value: t('slider.value'),
    },
    table: {
      selectAll: t('table.selectAll'),
      selectRow: t('table.selectRow'),
      actions: t('table.actions'),
      search: t('table.search'),
      loading: t('table.loading'),
      sortBy: (header) => t('table.sortBy', { header }),
    },
    tag: { remove: t('tag.remove') },
    tagInput: {
      suggestions: t('tagInput.suggestions'),
      create: (value) => t('tagInput.create', { value }),
      remove: (value) => t('tagInput.remove', { value }),
    },
    timePicker: {
      hour: t('timePicker.hour'),
      minute: t('timePicker.minute'),
      cycle24: t('timePicker.cycle24'),
      cycle12: t('timePicker.cycle12'),
      cancel: t('timePicker.cancel'),
      apply: t('timePicker.apply'),
      hoursColumn: t('timePicker.hoursColumn'),
      minutesColumn: t('timePicker.minutesColumn'),
      periodColumn: t('timePicker.periodColumn'),
      am: t('timePicker.am'),
      pm: t('timePicker.pm'),
      cycleGroup: t('timePicker.cycleGroup'),
    },
    versionOverlay: {
      copy: t('versionOverlay.copy'),
      copied: t('versionOverlay.copied'),
    },
    videoPlayer: {
      root: t('videoPlayer.root'),
      play: t('videoPlayer.play'),
      pause: t('videoPlayer.pause'),
      mute: t('videoPlayer.mute'),
      unmute: t('videoPlayer.unmute'),
      seek: t('videoPlayer.seek'),
      progress: t('videoPlayer.progress'),
      volume: t('videoPlayer.volume'),
      speed: t('videoPlayer.speed'),
      enterFullscreen: t('videoPlayer.enterFullscreen'),
      exitFullscreen: t('videoPlayer.exitFullscreen'),
    },
  };
}
